/**
 * 3PL fulfillment API client. Uses the app's axios instance (auth + CSRF),
 * with `silent: true` so screens show errors inline instead of a toast.
 *
 * Every function returns response.data. Errors are normalised by
 * `errorMessage(err)`; `err.flCode` carries the backend's stable code.
 */
import api from '../lib/api';

const S = { silent: true };

/** Training copy only: the coach's advice rides on 3PL responses (a header,
 * or `coach` in a refusal). Broadcast it for the coach panel. */
function coachFrom(response) {
  const items = [];
  const h = response?.headers?.['x-training-coach'];
  if (h) {
    try {
      const bin = atob(h.replace(/-/g, '+').replace(/_/g, '/'));
      items.push(...JSON.parse(new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)))));
    } catch { /* ignore a malformed header */ }
  }
  const c = response?.data?.coach;
  if (c && !items.some((x) => x.rule === c.rule)) items.push(c);
  if (items.length) window.dispatchEvent(new CustomEvent('fl-coach', { detail: items }));
}

async function req(method, url, body, extra = {}) {
  try {
    const cfg = { ...S, ...extra };
    const r = method === 'get' || method === 'delete'
      ? await api[method](url, cfg)
      : await api[method](url, body ?? {}, cfg);
    coachFrom(r);
    return r.data;
  } catch (err) {
    const d = err?.response?.data;
    err.flCode = d?.code;
    err.flDetails = d?.details;
    err.flStatus = err?.response?.status;
    coachFrom(err?.response);
    throw err;
  }
}

export function errorMessage(err, fallback = 'Something went wrong. Try again.') {
  if (!err) return '';
  if (!err.response) return 'Can’t reach ShipDesi. Check your connection and try again.';
  const d = err.response.data;
  if (typeof d?.detail === 'string') return d.detail;
  if (err.response.status === 403) return 'You don’t have access to this.';
  if (err.response.status === 404) return 'Not found, or you don’t have access to it.';
  return fallback;
}

/** One key per logical attempt; reuse it when retrying the same attempt. */
export function newIdemKey() {
  const rnd = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `fl-${rnd}`;
}

const idem = (key) => ({ headers: { 'Idempotency-Key': key || newIdemKey() } });
const qs = (params = {}) => {
  const p = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return p.length ? `?${new URLSearchParams(p).toString()}` : '';
};
const upload = (url, file, fields = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  Object.entries(fields).forEach(([k, v]) => fd.append(k, v ?? ''));
  return req('post', url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// ── Public ──────────────────────────────────────────────────────────────────
export const pub = {
  submitEnquiry: (fields, submissionKey, attachmentId) =>
    req('post', '/fl/public/enquiries', { fields, submission_key: submissionKey, attachment_id: attachmentId }),
  uploadAttachment: (file, submissionKey) =>
    upload('/fl/public/enquiry-attachments', file, { submission_key: submissionKey }),
  viewEnquiry: (ref, token) => req('get', `/fl/public/enquiries/${encodeURIComponent(ref)}`, null,
    { headers: { 'X-Edit-Token': token } }),
  correctEnquiry: (ref, token, fields) => req('put', `/fl/public/enquiries/${encodeURIComponent(ref)}`, { fields },
    { headers: { 'X-Edit-Token': token } }),
  viewQuote: (token) => req('get', `/fl/public/quotes/${encodeURIComponent(token)}`),
  acceptQuote: (token) => req('post', `/fl/quotes/${encodeURIComponent(token)}/accept`),
};

// ── Merchant ────────────────────────────────────────────────────────────────
const m = (mid) => `/fl/m/${encodeURIComponent(mid)}`;
export const merchant = {
  me: () => req('get', '/fl/me'),
  carriers: () => req('get', '/fl/carriers'),
  overview: (mid) => req('get', `${m(mid)}/overview`),
  onboarding: (mid) => req('get', `${m(mid)}/onboarding`),
  setOnboardingStep: (mid, step, status, note) => req('post', `${m(mid)}/onboarding/${step}`, { status, note }),
  notices: (mid, unread) => req('get', `${m(mid)}/notices${qs({ unread })}`),
  readNotices: (mid, ids) => req('post', `${m(mid)}/notices/read`, { notice_ids: ids }),
  exceptions: (mid, status = 'open') => req('get', `${m(mid)}/exceptions${qs({ status })}`),
  uploadEvidence: (mid, file, subjectType, subjectId, kind) =>
    upload(`${m(mid)}/evidence`, file, { subject_type: subjectType, subject_id: subjectId, kind: kind || 'document' }),
  // products
  skus: (mid, params) => req('get', `${m(mid)}/skus${qs(params)}`),
  createSku: (mid, body) => req('post', `${m(mid)}/skus`, body),
  sku: (mid, id) => req('get', `${m(mid)}/skus/${id}`),
  updateSku: (mid, id, body) => req('patch', `${m(mid)}/skus/${id}`, body),
  setSkuActive: (mid, id, active) => req('post', `${m(mid)}/skus/${id}/${active ? 'restore' : 'archive'}`),
  skuMovements: (mid, id, params) => req('get', `${m(mid)}/skus/${id}/movements${qs(params)}`),
  skuTemplate: (mid) => req('get', `${m(mid)}/skus/template.csv`),
  importSkus: (mid, csv, { mapping, commit } = {}) => req('post', `${m(mid)}/skus/import`, { csv, mapping, commit }),
  inventory: (mid, params) => req('get', `${m(mid)}/inventory${qs(params)}`),
  // inbound
  asns: (mid, params) => req('get', `${m(mid)}/asns${qs(params)}`),
  createAsn: (mid, body) => req('post', `${m(mid)}/asns`, body),
  asn: (mid, id) => req('get', `${m(mid)}/asns/${id}`),
  asnAction: (mid, id, action) => req('post', `${m(mid)}/asns/${id}/${action}`),
  asnTemplate: (mid) => req('get', `${m(mid)}/asns/template.csv`),
  importAsns: (mid, csv, commit) => req('post', `${m(mid)}/asns/import`, { csv, commit }),
  // orders
  orders: (mid, params) => req('get', `${m(mid)}/orders${qs(params)}`),
  createOrder: (mid, body) => req('post', `${m(mid)}/orders`, body),
  order: (mid, id) => req('get', `${m(mid)}/orders/${id}`),
  updateOrder: (mid, id, body) => req('patch', `${m(mid)}/orders/${id}`, body),
  orderAction: (mid, id, action, body) => req('post', `${m(mid)}/orders/${id}/${action}`, body || {}),
  releasePreview: (mid, ids) => req('post', `${m(mid)}/orders/release-preview`, { order_ids: ids }),
  bulkRelease: (mid, ids) => req('post', `${m(mid)}/orders/bulk-release`, { order_ids: ids }),
  orderTemplate: (mid) => req('get', `${m(mid)}/orders/template.csv`),
  importOrders: (mid, csv, commit) => req('post', `${m(mid)}/orders/import`, { csv, commit }),
  returnEligibility: (mid, orderId) => req('get', `${m(mid)}/orders/${orderId}/return-eligibility`),
  // shipments, returns
  shipments: (mid, params) => req('get', `${m(mid)}/shipments${qs(params)}`),
  shipment: (mid, id) => req('get', `${m(mid)}/shipments/${id}`),
  rmas: (mid, params) => req('get', `${m(mid)}/rmas${qs(params)}`),
  createRma: (mid, body) => req('post', `${m(mid)}/rmas`, body),
  rma: (mid, id) => req('get', `${m(mid)}/rmas/${id}`),
  rmaDecision: (mid, id, unitId, decision) => req('post', `${m(mid)}/rmas/${id}/units/${unitId}/decision`, { decision }),
  // billing
  charges: (mid, params) => req('get', `${m(mid)}/charges${qs(params)}`),
  charge: (mid, id) => req('get', `${m(mid)}/charges/${id}`),
  disputeCharge: (mid, id, text) => req('post', `${m(mid)}/charges/${id}/dispute`, { text }),
  invoices: (mid) => req('get', `${m(mid)}/invoices`),
  invoice: (mid, id) => req('get', `${m(mid)}/invoices/${id}`),
  payInvoice: (mid, id) => req('post', `${m(mid)}/invoices/${id}/pay`),
  payInvoiceFromWallet: (mid, id) => req('post', `${m(mid)}/invoices/${id}/pay-wallet`),
  /** The app's existing wallet top-up verification (an invoice payment lands in the wallet first). */
  verifyWalletTopup: (body) => req('post', '/wallet/topup/verify', body),
  rateCard: (mid) => req('get', `${m(mid)}/rate-card`),
  // reports, settings
  report: (mid, kind, params) => req('get', `${m(mid)}/reports/${kind}${qs(params)}`),
  reportCsv: (mid, kind, params) => req('get', `${m(mid)}/reports/${kind}${qs({ ...params, format: 'csv' })}`),
  settings: (mid) => req('get', `${m(mid)}/settings`),
  updateSettings: (mid, body) => req('patch', `${m(mid)}/settings`, body),
  invite: (mid, email, role) => req('post', `${m(mid)}/team/invites`, { email, role }),
  cancelInvite: (mid, inviteId) => req('delete', `${m(mid)}/team/invites/${inviteId}`),
  removeMember: (mid, userId) => req('delete', `${m(mid)}/team/${userId}`),
  // store connections (Shopify connector is off unless the server enables it)
  connections: (mid) => req('get', `${m(mid)}/connections`),
  startShopify: (mid, shop) => req('post', `${m(mid)}/connections/shopify/start`, { shop }),
  disconnect: (mid, cid) => req('post', `${m(mid)}/connections/${cid}/disconnect`),
  syncConnection: (mid, cid) => req('post', `${m(mid)}/connections/${cid}/sync`),
  variants: (mid, cid, params) => req('get', `${m(mid)}/connections/${cid}/variants${qs(params)}`),
  mapVariant: (mid, cid, externalId, skuId) =>
    req('post', `${m(mid)}/connections/${cid}/variants/map`, { external_id: externalId, sku_id: skuId }),
};

// ── Admin (3PL Ops) ─────────────────────────────────────────────────────────
const A = '/fl/admin';
export const admin = {
  tower: (params) => req('get', `${A}/tower${qs(params)}`),
  search: (q) => req('get', `${A}/search${qs({ q })}`),
  enquiries: (params) => req('get', `${A}/enquiries${qs(params)}`),
  enquiry: (id) => req('get', `${A}/enquiries/${id}`),
  moveEnquiry: (id, body) => req('post', `${A}/enquiries/${id}/move`, body),
  noteEnquiry: (id, text) => req('post', `${A}/enquiries/${id}/notes`, { text }),
  saveQuote: (id, body) => req('post', `${A}/enquiries/${id}/quotes`, body),
  sendQuote: (quoteId) => req('post', `${A}/quotes/${quoteId}/send`),
  merchants: () => req('get', `${A}/merchants`),
  createMerchant: (body) => req('post', `${A}/merchants`, body),
  merchant: (mid) => req('get', `${A}/merchants/${mid}`),
  setHold: (mid, kind, reason) => req('post', `${A}/merchants/${mid}/holds/${kind}`, { reason }),
  reviewStep: (mid, step, status, note) => req('post', `${A}/merchants/${mid}/onboarding/${step}`, { status, note }),
  addRateCard: (mid, body) => req('post', `${A}/merchants/${mid}/rate-cards`, body),
  merchantSettings: (mid, body) => req('patch', `${A}/merchants/${mid}/settings`, body),
  addMember: (mid, email, role) => req('post', `${A}/merchants/${mid}/members`, { email, role }),
  shopifyApps: (mid) => req('get', `${A}/shopify-apps?merchant_id=${encodeURIComponent(mid)}`),
  addShopifyApp: (body) => req('post', `${A}/shopify-apps`, body),
  removeShopifyApp: (appId) => req('delete', `${A}/shopify-apps/${appId}`),
  warehouses: () => req('get', `${A}/warehouses`),
  createWarehouse: (body) => req('post', `${A}/warehouses`, body),
  locations: (params) => req('get', `${A}/locations${qs(params)}`),
  createLocation: (body) => req('post', `${A}/locations`, body),
  staff: () => req('get', `${A}/staff`),
  setStaff: (userId, roles, warehouseIds) => req('put', `${A}/staff/${userId}`, { roles, warehouse_ids: warehouseIds }),
  settings: () => req('get', `${A}/settings`),
  updateSettings: (body) => req('patch', `${A}/settings`, body),
  health: () => req('get', `${A}/health`),
  jobs: (status) => req('get', `${A}/jobs${qs({ status })}`),
  retryJob: (id) => req('post', `${A}/jobs/${id}/retry`),
  asns: (params) => req('get', `${A}/asns${qs(params)}`),
  asn: (id) => req('get', `${A}/asns/${id}`),
  asnAction: (id, action, body) => req('post', `${A}/asns/${id}/${action}`, body || {}),
  inventory: (params) => req('get', `${A}/inventory${qs(params)}`),
  skus: (merchantId, q) => req('get', `${A}/skus${qs({ merchant_id: merchantId, q })}`),
  adjust: (body) => req('post', `${A}/inventory/adjust`, body),
  transfers: (status) => req('get', `${A}/transfers${qs({ status })}`),
  createTransfer: (body) => req('post', `${A}/transfers`, body),
  cancelTransfer: (id) => req('post', `${A}/transfers/${id}/cancel`),
  counts: (status) => req('get', `${A}/counts${qs({ status })}`),
  createCount: (location, reason) => req('post', `${A}/counts`, { location, reason }),
  approveCount: (id) => req('post', `${A}/counts/${id}/approve`),
  corrections: (status) => req('get', `${A}/corrections${qs({ status })}`),
  approveCorrection: (id) => req('post', `${A}/corrections/${id}/approve`),
  orders: (params) => req('get', `${A}/orders${qs(params)}`),
  order: (id) => req('get', `${A}/orders/${id}`),
  orderAction: (id, action, body) => req('post', `${A}/orders/${id}/${action}`, body || {}),
  pickWave: (params) => req('get', `${A}/pick-wave${qs(params)}`),
  shipments: (params) => req('get', `${A}/shipments${qs(params)}`),
  shipment: (id) => req('get', `${A}/shipments/${id}`),
  shipmentAction: (id, action, body) => req('post', `${A}/shipments/${id}/${action}`, body || {}),
  manifests: (status) => req('get', `${A}/manifests${qs({ status })}`),
  rmas: (params) => req('get', `${A}/rmas${qs(params)}`),
  rma: (id) => req('get', `${A}/rmas/${id}`),
  rmaDecision: (id, unitId, decision) => req('post', `${A}/rmas/${id}/units/${unitId}/decision`, { decision }),
  exceptions: (params) => req('get', `${A}/exceptions${qs(params)}`),
  exception: (id) => req('get', `${A}/exceptions/${id}`),
  exceptionAction: (id, action, body) => req('post', `${A}/exceptions/${id}/${action}`, body || {}),
  billingPreview: (merchantId, period) => req('get', `${A}/billing/preview${qs({ merchant_id: merchantId, period })}`),
  draftInvoice: (merchantId, period) => req('post', `${A}/billing/draft`, { merchant_id: merchantId, period }),
  settleNow: (merchantId) => req('post', `${A}/merchants/${encodeURIComponent(merchantId)}/settle`),
  invoices: (params) => req('get', `${A}/invoices${qs(params)}`),
  invoiceAction: (id, action, body) => req('post', `${A}/invoices/${id}/${action}`, body || {}),
  charges: (params) => req('get', `${A}/charges${qs(params)}`),
  rejectDispute: (chargeId, reason) => req('post', `${A}/charges/${chargeId}/reject-dispute`, { reason }),
  report: (kind, params) => req('get', `${A}/reports/${kind}${qs(params)}`),
  reportCsv: (kind, params) => req('get', `${A}/reports/${kind}${qs({ ...params, format: 'csv' })}`),
};

// ── Warehouse floor ─────────────────────────────────────────────────────────
const F = '/fl/floor';
export const floor = {
  tasks: (params) => req('get', `${F}/tasks${qs(params)}`),
  task: (id) => req('get', `${F}/tasks/${id}`),
  startTask: (id) => req('post', `${F}/tasks/${id}/start`),
  staff: (perm, warehouseId) => req('get', `${F}/staff${qs({ perm, warehouse_id: warehouseId })}`),
  assignTask: (id, userId) => req('post', `${F}/tasks/${id}/assign`, { user_id: userId }),
  scan: (code) => req('get', `${F}/scan/${encodeURIComponent(code)}`),
  asnByRef: (ref) => req('get', `${F}/asns/${encodeURIComponent(ref)}`),
  receive: (asnId, body, key) => req('post', `${F}/asns/${asnId}/receive`, body, idem(key)),
  completeCarton: (asnId, cartonId) => req('post', `${F}/asns/${asnId}/cartons/${cartonId}/complete`),
  receiveExcess: (asnId, body, key) => req('post', `${F}/asns/${asnId}/excess`, body, idem(key)),
  unknownArrival: (body) => req('post', `${F}/unknown-arrival`, body),
  uploadEvidence: (file, fields) => upload(`${F}/evidence`, file, fields),
  putaway: (taskId, body, key) => req('post', `${F}/putaway/${taskId}`, body, idem(key)),
  pick: (taskId, body, key) => req('post', `${F}/pick/${taskId}`, body, idem(key)),
  short: (taskId, body, key) => req('post', `${F}/pick/${taskId}/short`, body, idem(key)),
  pack: (taskId, body, key) => req('post', `${F}/pack/${taskId}`, body, idem(key)),
  putBack: (taskId, body, key) => req('post', `${F}/putback/${taskId}`, body, idem(key)),
  completeTransfer: (id, body, key) => req('post', `${F}/transfers/${id}/complete`, body, idem(key)),
  submitCount: (id, counted) => req('post', `${F}/counts/${id}/submit`, { counted }),
  requestCorrection: (id, body, key) => req('post', `${F}/counts/${id}/correction`, body, idem(key)),
  bin: (code) => req('get', `${F}/bins/${encodeURIComponent(code)}`),
  countLookup: (id, code) => req('get', `${F}/counts/${id}/lookup${qs({ code })}`),
  rmaByRef: (ref) => req('get', `${F}/rmas/${encodeURIComponent(ref)}`),
  rmaById: (id) => req('get', `${F}/rma-by-id/${encodeURIComponent(id)}`),
  rmaReceive: (id, body, key) => req('post', `${F}/rmas/${id}/receive`, body, idem(key)),
  rmaGrade: (id, unitId, body) => req('post', `${F}/rmas/${id}/units/${unitId}/grade`, body),
  completeUnitTask: (taskId, body, key) => req('post', `${F}/unit-tasks/${taskId}/complete`, body, idem(key)),
  handover: (manifestId, body, key) => req('post', `${F}/manifests/${manifestId}/handover`, body, idem(key)),
};

/** Commercial invoice + CN23 PDF for an export order, as an object URL. */
export async function customsDocsUrl(orderId, merchantId) {
  const path = merchantId ? `/fl/m/${merchantId}/orders/${orderId}` : `/fl/admin/orders/${orderId}`;
  const r = await api.get(`${path}/customs-docs.pdf`, { ...S, responseType: 'blob' });
  return URL.createObjectURL(r.data);
}

// ── Training copy ───────────────────────────────────────────────────────────
const T = '/fl/training';
export const training = {
  status: () => req('get', `${T}/status`),
  scenario: (type, id) => req('get', `${T}/scenario${qs({ type, id })}`),
  runDay: (count) => req('post', `${T}/run-day`, count === undefined ? {} : { count }),
  setOrdersPerDay: (n) => req('patch', `${T}/settings`, { orders_per_day: n }),
  reset: () => req('post', `${T}/reset`, { confirm: 'RESET' }),
  trainees: () => req('get', `${T}/trainees`),
  events: (userId) => req('get', `${T}/events${qs({ user_id: userId })}`),
  addTrainee: (email, role) => req('post', `${T}/trainees`, { email, role }),
};

/** Fetch a private 3PL file as an object URL (auth header required). */
export async function fileObjectUrl(evidenceId) {
  const r = await api.get(`/fl/files/${evidenceId}`, { ...S, responseType: 'blob' });
  return URL.createObjectURL(r.data);
}
